import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, RotateCcw, Search, ShieldCheck, Lock, Link2 } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/axios';
import { usePermissions, permissionError } from '../../context/PermissionsContext';
import './RulesPermissions.css';

const SCOPE_ORDER = ['NONE', 'OWN', 'ASSIGNED', 'TEAM', 'ALL'];
const ROLE_HINTS = {
  ADMIN: 'Company admins',
  MANAGER: 'Hiring managers',
  RECRUITER: 'Recruiters',
  VIEWER: 'Read-only users',
};
const TEAM_ROLES = ['RECRUITER', 'VIEWER'];
const VISIBILITY_LABELS = { OWN: 'Own only', ROLE_BASED: 'Role based', COMPANY: 'Company wide' };
const ACCESS_LABELS = { EDITOR: 'Editor', VIEWER: 'Viewer' };

const rank = (s) => SCOPE_ORDER.indexOf(s);

function optionLabel(policy, value) {
  if (policy.key === 'data.visibility') return VISIBILITY_LABELS[value] || value;
  if (policy.key === 'assign.default_access') return ACCESS_LABELS[value] || value;
  return value;
}

function valueLabel(kind, value, scopes) {
  if (kind === 'policy-bool') return value === 'true' ? 'On' : 'Off';
  if (kind === 'scope') return scopes.find((s) => s.value === value)?.label || value;
  return String(value);
}

function Toggle({ on, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      className={`rp-toggle${on ? ' on' : ''}`}
      onClick={() => !disabled && onChange(!on)}
    >
      <span className="rp-toggle-knob" />
    </button>
  );
}

/** Company-wide (or team) switch row. */
function PolicyRow({ policy, value, companyValue, editable, teamMode, onChange }) {
  const lockedByAdmin = teamMode && !policy.teamEditable;
  const disabled = !editable || lockedByAdmin;
  let control;
  if (policy.type === 'BOOLEAN') {
    control = <Toggle on={value === 'true'} disabled={disabled} label={policy.label} onChange={(on) => onChange(on ? 'true' : 'false')} />;
  } else if (policy.type === 'CHOICE') {
    control = (
      <select className="rp-select" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        {policy.options.map((o) => <option key={o} value={o}>{optionLabel(policy, o)}</option>)}
      </select>
    );
  } else {
    control = (
      <input
        className="rp-number"
        type="number"
        min={0}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(String(Math.max(0, parseInt(e.target.value || '0', 10) || 0)))}
      />
    );
  }
  return (
    <div className="rp-policy">
      <div className="rp-policy-text">
        <div className="rp-policy-label">
          {policy.label}
          {lockedByAdmin && <span className="rp-chip"><Lock size={10} /> Set by admin</span>}
          {teamMode && policy.teamEditable && value !== companyValue && <span className="rp-chip team">Team rule</span>}
        </div>
        <div className="rp-policy-desc">{policy.description}</div>
        {teamMode && policy.teamEditable && (
          <div className="rp-policy-hint">Company setting: {policy.type === 'BOOLEAN' ? (companyValue === 'true' ? 'On' : 'Off') : optionLabel(policy, companyValue)}. Teams can only make this stricter.</div>
        )}
      </div>
      <div className="rp-policy-control">{control}</div>
    </div>
  );
}

/** One permission cell: checkbox for on/off actions, scope dropdown for position actions. */
function PermissionCell({ perm, role, value, original, ceiling, editable, scopes, onChange }) {
  const changed = value !== original;
  const locked = perm.lockedFor.includes(role);
  if (!perm.positionScoped) {
    const allowedToTurnOn = !ceiling || ceiling === 'ALL';
    return (
      <td className={`rp-cell${changed ? ' changed' : ''}`}>
        <label className="rp-check" title={locked ? 'Admins always keep this' : undefined}>
          <input
            type="checkbox"
            checked={value === 'ALL'}
            disabled={!editable || locked || (value !== 'ALL' && !allowedToTurnOn)}
            onChange={(e) => onChange(e.target.checked ? 'ALL' : 'NONE')}
          />
          <span className="rp-check-box" />
        </label>
      </td>
    );
  }
  const options = scopes.filter((s) => !ceiling || rank(s.value) <= rank(ceiling));
  return (
    <td className={`rp-cell${changed ? ' changed' : ''}`}>
      <select
        className={`rp-scope s-${value.toLowerCase()}`}
        value={value}
        disabled={!editable}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
    </td>
  );
}

/**
 * "Apply these rules to Wizetok" — only when the company has both ReachOut and Wizetok.
 * While on, Wizetok follows the matching ReachOut rules (positions ↔ campaigns, candidates,
 * data visibility, delete / export, users). Wizetok-only rules stay in Wizetok.
 */
function WizetokLinkCard({ link, busy, onChange }) {
  if (!link || (!link.available && !link.enabled)) return null;
  return (
    <section className="rp-card">
      <div className="rp-policy">
        <div className="rp-policy-text">
          <div className="rp-policy-label"><Link2 size={14} /> Apply these rules to Wizetok</div>
          <div className="rp-policy-desc">
            {link.enabled
              ? 'On: Wizetok follows these Rules & Permissions for everything both products share (positions, candidates, data visibility, delete / export, users). Wizetok-only rules (questions, tests, interviews) are still set in Wizetok.'
              : 'Your company has both ReachOut and Wizetok. Turn this on so Wizetok uses the same Rules & Permissions and you only manage them here.'}
          </div>
          {link.error && <div className="rp-policy-hint">{link.error}</div>}
          {link.enabled && link.available === false && (
            <div className="rp-policy-hint">Wizetok is no longer enabled for your company. Turn this off to let each product keep its own rules.</div>
          )}
        </div>
        <div className="rp-policy-control">
          <Toggle on={!!link.enabled} disabled={!link.editable || busy} label="Apply these rules to Wizetok" onChange={onChange} />
        </div>
      </div>
    </section>
  );
}

function ReviewModal({ changes, saving, onCancel, onConfirm }) {
  return (
    <div className="rp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="rp-modal">
        <h3>Review {changes.length} change{changes.length === 1 ? '' : 's'}</h3>
        <p className="rp-muted">These apply as soon as you save. Everyone affected sees the new rules on their next page load.</p>
        <ul className="rp-change-list">
          {changes.map((c) => (
            <li key={c.id}>
              <span className="rp-change-what">{c.what}</span>
              <span className="rp-change-from">{c.from}</span>
              <span className="rp-arrow">→</span>
              <span className="rp-change-to">{c.to}</span>
            </li>
          ))}
        </ul>
        <div className="rp-modal-actions">
          <button type="button" className="rp-btn ghost" onClick={onCancel} disabled={saving}>Back</button>
          <button type="button" className="rp-btn primary" onClick={onConfirm} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RulesPermissions() {
  const perms = usePermissions();
  const isAdmin = perms.canEditCompanyRules;
  const isManager = perms.hasRole('MANAGER');

  const [catalog, setCatalog] = useState(null);
  const [mode, setMode] = useState(isAdmin ? 'company' : 'team');
  const [managers, setManagers] = useState([]);
  const [teamOf, setTeamOf] = useState('');
  const [server, setServer] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [wizetokLink, setWizetokLink] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);

  useEffect(() => {
    if (perms.loaded) setMode(perms.canEditCompanyRules ? 'company' : 'team');
  }, [perms.loaded, perms.canEditCompanyRules]);

  useEffect(() => {
    api.get('/rules/catalog').then((res) => setCatalog(res.data)).catch(() => toast.error('Could not load the rules catalog'));
  }, []);

  useEffect(() => {
    if (!isAdmin && !isManager) return;
    api.get('/rules/wizetok-link').then((res) => setWizetokLink(res.data)).catch(() => setWizetokLink(null));
  }, [isAdmin, isManager]);

  const toggleWizetokLink = async (on) => {
    const question = on
      ? 'Apply these Rules & Permissions to Wizetok too? The shared Wizetok rules will follow ReachOut from now on.'
      : 'Stop applying these rules to Wizetok? Wizetok keeps a copy of the current values and can change them on its own again.';
    if (!window.confirm(question)) return;
    setLinkBusy(true);
    try {
      const res = await api.put('/rules/wizetok-link', { enabled: on });
      setWizetokLink(res.data);
      toast.success(on ? 'Wizetok now follows these rules' : 'Wizetok manages its own rules again');
    } catch (err) {
      toast.error(permissionError(err, 'Could not change the Wizetok setting'));
    } finally {
      setLinkBusy(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/rules/managers').then((res) => setManagers(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, [isAdmin]);

  const teamTarget = mode === 'team' ? (isAdmin ? teamOf : perms.email) : null;

  const load = useCallback(async () => {
    if (mode === 'team' && !teamTarget) {
      setServer(null);
      setDraft(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = mode === 'company'
        ? await api.get('/rules/company')
        : await api.get('/rules/team', { params: { manager: teamTarget } });
      setServer(res.data);
      setDraft({ permissions: JSON.parse(JSON.stringify(res.data.permissions)), policies: { ...res.data.policies } });
    } catch (err) {
      toast.error(permissionError(err, 'Could not load rules'));
      setServer(null);
      setDraft(null);
    } finally {
      setLoading(false);
    }
  }, [mode, teamTarget]);

  useEffect(() => { load(); }, [load]);

  const teamMode = mode === 'team';
  const editable = !!server?.editable;
  const scopes = useMemo(() => catalog?.scopes || [], [catalog]);

  const changes = useMemo(() => {
    if (!server || !draft || !catalog) return [];
    const out = [];
    catalog.policies.forEach((p) => {
      const before = server.policies[p.key];
      const after = draft.policies[p.key];
      if (before !== after) {
        const kind = p.type === 'BOOLEAN' ? 'policy-bool' : 'policy';
        out.push({
          id: `p:${p.key}`, type: 'policy', key: p.key, value: after,
          what: p.label,
          from: p.type === 'CHOICE' ? optionLabel(p, before) : valueLabel(kind, before, scopes),
          to: p.type === 'CHOICE' ? optionLabel(p, after) : valueLabel(kind, after, scopes),
        });
      }
    });
    catalog.permissions.forEach((p) => {
      catalog.roles.forEach((r) => {
        const before = server.permissions[p.key]?.[r.value];
        const after = draft.permissions[p.key]?.[r.value];
        if (before !== after) {
          out.push({
            id: `x:${p.key}:${r.value}`, type: 'permission', key: p.key, role: r.value, value: after,
            what: `${r.label} · ${p.label}`,
            from: p.positionScoped ? valueLabel('scope', before, scopes) : (before === 'ALL' ? 'Allowed' : 'Not allowed'),
            to: p.positionScoped ? valueLabel('scope', after, scopes) : (after === 'ALL' ? 'Allowed' : 'Not allowed'),
          });
        }
      });
    });
    return out;
  }, [server, draft, catalog, scopes]);

  const setPolicy = (key, value) => setDraft((d) => ({ ...d, policies: { ...d.policies, [key]: value } }));
  const setPerm = (key, role, value) => setDraft((d) => ({
    ...d,
    permissions: { ...d.permissions, [key]: { ...d.permissions[key], [role]: value } },
  }));

  const save = async () => {
    const body = { permissions: {}, policies: {} };
    changes.forEach((c) => {
      if (c.type === 'policy') body.policies[c.key] = c.value;
      else body.permissions[c.key] = { ...(body.permissions[c.key] || {}), [c.role]: c.value };
    });
    setSaving(true);
    try {
      const res = mode === 'company'
        ? await api.put('/rules/company', body)
        : await api.put('/rules/team', body, { params: { manager: teamTarget } });
      setServer(res.data);
      setDraft({ permissions: JSON.parse(JSON.stringify(res.data.permissions)), policies: { ...res.data.policies } });
      setReviewing(false);
      toast.success(`Saved ${changes.length} change${changes.length === 1 ? '' : 's'}`);
      perms.refresh();
    } catch (err) {
      toast.error(permissionError(err, 'Could not save rules'));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const what = teamMode ? 'this team\'s rules back to the company rules' : 'every company rule back to the HireCrux defaults';
    if (!window.confirm(`Reset ${what}? This cannot be undone (the audit log keeps a record).`)) return;
    setSaving(true);
    try {
      const res = mode === 'company'
        ? await api.post('/rules/company/reset')
        : await api.post('/rules/team/reset', null, { params: { manager: teamTarget } });
      setServer(res.data);
      setDraft({ permissions: JSON.parse(JSON.stringify(res.data.permissions)), policies: { ...res.data.policies } });
      toast.success('Rules reset');
      perms.refresh();
    } catch (err) {
      toast.error(permissionError(err, 'Could not reset rules'));
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo(() => {
    if (!catalog) return [];
    const q = search.trim().toLowerCase();
    const map = new Map();
    catalog.permissions.forEach((p) => {
      if (q && !`${p.label} ${p.description} ${p.group}`.toLowerCase().includes(q)) return;
      if (onlyChanged && draft && server) {
        const changedHere = catalog.roles.some((r) => draft.permissions[p.key]?.[r.value] !== p.defaults[r.value]);
        if (!changedHere) return;
      }
      if (!map.has(p.group)) map.set(p.group, []);
      map.get(p.group).push(p);
    });
    return [...map.entries()];
  }, [catalog, search, onlyChanged, draft, server]);

  if (!perms.loaded || !catalog) return <div className="rp-empty">Loading rules…</div>;

  if (!isAdmin && !isManager) {
    return <div className="rp-empty">Only admins and managers can open Rules &amp; Permissions.</div>;
  }

  return (
    <div className="rp">
      <div className="rp-head">
        <div>
          <h2><ShieldCheck size={20} /> Rules &amp; Permissions</h2>
          <p className="rp-muted">
            {teamMode
              ? 'Rules for your team: the recruiters and viewers you (or they) invited. Team rules can only be stricter than the company rules.'
              : 'Decide what each role can do across the company. Defaults match how HireCrux worked before these rules existed.'}
          </p>
        </div>
        <div className="rp-head-actions">
          <div className="rp-seg" role="tablist">
            {isAdmin && (
              <button type="button" role="tab" aria-selected={mode === 'company'} className={mode === 'company' ? 'on' : ''} onClick={() => setMode('company')}>
                Company rules
              </button>
            )}
            {(isManager || isAdmin) && (
              <button type="button" role="tab" aria-selected={mode === 'team'} className={mode === 'team' ? 'on' : ''} onClick={() => setMode('team')}>
                {isAdmin ? 'Team rules' : 'My team\'s rules'}
              </button>
            )}
          </div>
          {teamMode && isAdmin && (
            <select className="rp-select" value={teamOf} onChange={(e) => setTeamOf(e.target.value)}>
              <option value="">Choose a manager…</option>
              {isManager && perms.email && <option value={perms.email}>My team</option>}
              {managers.filter((m) => m.email !== perms.email).map((m) => (
                <option key={m.email} value={m.email}>{m.name} ({m.email})</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {!teamMode && <WizetokLinkCard link={wizetokLink} busy={linkBusy} onChange={toggleWizetokLink} />}

      {teamMode && !teamTarget && <div className="rp-empty">Choose a manager to see or change their team&apos;s rules.</div>}
      {loading && (mode === 'company' || teamTarget) && <div className="rp-empty">Loading…</div>}

      {!loading && server && draft && (
        <>
          {!editable && <div className="rp-banner">You can view these rules. Only an admin can change company rules.</div>}

          <section className="rp-card">
            <div className="rp-card-head">
              <h3>{teamMode ? 'Team rules' : 'Company-wide rules'}</h3>
              <span className="rp-muted">Switches that apply to everyone{teamMode ? ' on this team' : ''}</span>
            </div>
            {catalog.policies.map((p) => (
              <PolicyRow
                key={p.key}
                policy={p}
                value={draft.policies[p.key]}
                companyValue={server.companyPolicies ? server.companyPolicies[p.key] : server.policies[p.key]}
                editable={editable}
                teamMode={teamMode}
                onChange={(v) => setPolicy(p.key, v)}
              />
            ))}
          </section>

          <section className="rp-card">
            <div className="rp-card-head">
              <h3>Role permissions</h3>
              <span className="rp-muted">
                People with several roles get everything any of their roles allows.
                {teamMode && ' Only the Recruiter and Viewer columns can be changed for a team.'}
              </span>
            </div>
            <div className="rp-toolbar">
              <div className="rp-search">
                <Search size={14} />
                <input placeholder="Search actions…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <label className="rp-inline">
                <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />
                Only show actions that differ from the defaults
              </label>
            </div>
            <div className="rp-legend">
              <span><b>Own positions</b>: positions they created</span>
              <span><b>Own + assigned</b>: plus positions they are assigned to as Editor</span>
              <span><b>Team positions</b>: plus positions created by people they invited</span>
              <span><b>All positions</b>: every position in the company</span>
            </div>
            <div className="rp-table-wrap">
              <table className="rp-table">
                <thead>
                  <tr>
                    <th className="rp-col-action">Action</th>
                    {catalog.roles.map((r) => (
                      <th key={r.value} className={teamMode && !TEAM_ROLES.includes(r.value) ? 'dim' : ''}>
                        <div>{r.label}</div>
                        <div className="rp-th-hint">{ROLE_HINTS[r.value]}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                {groups.map(([group, items]) => (
                  <tbody key={group}>
                    <tr className="rp-group-row" onClick={() => setCollapsed((c) => ({ ...c, [group]: !c[group] }))}>
                      <td colSpan={1 + catalog.roles.length}>
                        <span className="rp-group-label" aria-expanded={!collapsed[group]}>
                          {collapsed[group] ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                          <span>{group}</span>
                          <span className="rp-count">{items.length}</span>
                        </span>
                      </td>
                    </tr>
                    {!collapsed[group] && items.map((p) => (
                      <tr key={p.key}>
                        <td className="rp-col-action">
                          <div className="rp-action-label">{p.label}</div>
                          <div className="rp-action-desc">{p.description}</div>
                        </td>
                        {catalog.roles.map((r) => {
                          const roleEditable = editable && (!teamMode || TEAM_ROLES.includes(r.value));
                          const ceiling = teamMode && server.companyPermissions ? server.companyPermissions[p.key][r.value] : null;
                          return (
                            <PermissionCell
                              key={r.value}
                              perm={p}
                              role={r.value}
                              value={draft.permissions[p.key][r.value]}
                              original={server.permissions[p.key][r.value]}
                              ceiling={ceiling}
                              editable={roleEditable}
                              scopes={scopes}
                              onChange={(v) => setPerm(p.key, r.value, v)}
                            />
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
              {groups.length === 0 && <div className="rp-empty small">No actions match.</div>}
            </div>
          </section>

          {editable && (
            <div className="rp-footer">
              <button type="button" className="rp-btn ghost" onClick={reset} disabled={saving}>
                <RotateCcw size={13} /> {teamMode ? 'Reset to company rules' : 'Reset to defaults'}
              </button>
            </div>
          )}

          {editable && changes.length > 0 && (
            <div className="rp-savebar">
              <span><b>{changes.length}</b> unsaved change{changes.length === 1 ? '' : 's'}</span>
              <div>
                <button type="button" className="rp-btn ghost" onClick={() => setDraft({ permissions: JSON.parse(JSON.stringify(server.permissions)), policies: { ...server.policies } })}>
                  Discard
                </button>
                <button type="button" className="rp-btn primary" onClick={() => setReviewing(true)}>Review &amp; save</button>
              </div>
            </div>
          )}

          {reviewing && (
            <ReviewModal changes={changes} saving={saving} onCancel={() => setReviewing(false)} onConfirm={save} />
          )}
        </>
      )}
    </div>
  );
}
